<template>
  <div :class="['lesson-join', { scale: firefoxScale }]">
    <LessonHeader :enable-theme="false" :hide-display="'hide_center'" />
    <div class="device-container">
      <div class="left-side">
        <el-row>
          <div :class="['device-video', { off: !cameraEnabled, blur: blurEnabled, loading: blurLoading }]">
            <div class="[video-wrapper]">
              <!-- Shows video with virtual background -->
              <canvas class="virtual-bg-video"></canvas>
              <video 
                id="local-video" 
                class="for-process-video video-call-feed" 
                style="transform: scaleX(-1)" 
                :controls="false" 
                autoplay 
                playsinline
              ></video>
              <div v-if="!cameraEnabled || hasCameraError" id="video-off">
                <div class="avatar">{{ localNickname.charAt(0).toUpperCase() }}</div>
              </div>
            </div>
            <div class="buttons">
              <el-tooltip :disabled="!hasMicError || isMobileDevice" :content="$t('deviceErrorGuide.tooltip')" placement="top">
                <el-button
                  @click.prevent="toggleMic"
                  :class="{ 
                    'device-mute': !micEnabled && !hasMicError,
                    'device-error': hasMicError 
                  }"
                  circle
                >
                  <img :src="micIconSource(micEnabled)" class="device-icon" /> 
                  <img :src="ExcalamationSvg" class="setting-error-exclamation" v-if="hasMicError"/>
                </el-button>
              </el-tooltip>
              <el-tooltip :disabled="!hasCameraError || isMobileDevice" :content="$t('deviceErrorGuide.tooltip')" placement="top">
                <el-button
                  @click.prevent="toggleCamera"
                  :class="{ 
                    'device-mute': !cameraEnabled && !hasCameraError,
                    'device-error': hasCameraError
                  }"
                  circle
                >
                  <img :src="cameraIconSource(cameraEnabled)" class="device-icon" />
                  <img 
                    :src="ExcalamationSvg" 
                    class="setting-error-exclamation" 
                    v-if="hasCameraError"
                  />
                </el-button>
              </el-tooltip>
              <el-button
                v-if="!isSafariBrowser"
                :class="['blur-button', { enabled: blurEnabled }]"
                :disabled="disableCameraSelection || (cameraId == '' && !isMobileDevice)"
                @click.prevent="toggleBlur"
                circle
              >
                <img :src="blurIconSource()" class="device-icon" />
              </el-button>
            </div>
          </div>
        </el-row>
      </div>

      <div class="right-side">
        <div class="device-check-text">
          <h1>{{ $t('deviceCheck.title') }}</h1>
          <p v-html="$t('deviceCheck.subtitle')"></p>
        </div>

        <div class="device-selection initial">
          <div style="position: relative">
            <el-select
              ref="videoinput"
              v-model="cameraModel"
              :class="[
                'camera-selection', { 
                'unavailable': disableCameraSelection, 
                'device-selection-error': hasCameraError 
              }]"
              popper-class="join__sub-select"
              :placeholder="$t(cameraSelectPlaceHolder, { device: $t('deviceErrorGuide.camera') })"
              :disabled="disableCameraSelection || !devicesReady"
              @change="onManualCameraChange"
            >
              <template v-if="cameras.length > 0">
                <el-option
                  v-for="camera in cameras"
                  :key="camera.deviceId"
                  :label="camera.label"
                  :value="camera.deviceId"
                >
                  <div>
                    {{ camera.label }}
                    <div class="defaultDevice" v-if="camera.isDefault">{{ $t('systemDefault') }}</div>
                  </div>
                </el-option>
              </template>
            </el-select>
            <div class="device-skeleton-shimmer" v-if="!devicesReady"></div>
          </div>
          <div class="mic-row">
            <el-select
              ref="audioinput"
              v-model="micModel"
              :class="[
                'mic-selection', { 
                'unavailable': disableMicSelection , 
                'device-selection-error': hasMicError 
              }]"
              popper-class="join__sub-select"
              :placeholder="$t(micSelectPlaceHolder, { device: $t('deviceErrorGuide.mic') })"
              :disabled="disableMicSelection || !devicesReady"
              @change="onManualMicChange"
            >
              <template v-if="mics.length > 0">
                <el-option v-for="mic in mics" :key="mic.deviceId" :label="mic.label" :value="mic.deviceId" >
                  <div>
                    {{ mic.label }}
                    <div class="defaultDevice" v-if="mic.isDefault">{{ $t('systemDefault') }}</div>
                  </div>
                </el-option>
              </template>
            </el-select>
            <div class="device-skeleton-shimmer" v-if="!devicesReady"></div>
            <Waveform :stream="audioStream" />
          </div>
          <div class="speaker-row">
            <el-select
              ref="audiooutput"
              v-model="speakerId"
              :class="['speaker-selection']"
              :popper-class="speakers.length > 0 ? 'join__sub-select' : 'hide__sub-select'"
              :placeholder="$t(speakerSelectPlaceHolder)"
              :disabled="disableSpeakerSelection || !devicesReady"
              @change="onManualSpeakerChange"
            >
              <template v-if="speakers.length > 0">
                <el-option
                  v-for="speaker in speakers"
                  :key="speaker.deviceId"
                  :label="speaker.label || ''"
                  :value="speaker.deviceId"
                >
                  <div>
                    {{ speaker.label }}
                    <div class="defaultDevice" v-if="speaker.isDefault">{{ $t('systemDefault') }}</div>
                  </div>
                </el-option>
              </template>
            </el-select>
            <div class="device-skeleton-shimmer" v-if="!devicesReady"></div>
            <div :class="['speaker_btn']" @click.prevent="playSound">
              <a type="primary">{{ $t('deviceCheck.speakerTest') }}</a>
            </div>
            <audio style="display:none">
              <source src="~@/assets/sounds/AudioSampleTrainers.mp3" type="audio/mpeg" />
            </audio>
          </div>
        </div>
        <div class="error_message" v-if="displayBottomErrorMessage()">
          <p>{{ $t('deviceCheck.bottomErrorMessage') }}</p>
        </div>
      </div>
    </div>
    <div class="join_btn" :class="{ disabled: !initialized }">
      <a type="primary" size="large" @click="joinLesson">{{ $t(joinBtnText) }}</a>
    </div>
    <div class="foot_notes" v-if="isStudent">
      <a :href="faqLink" target="_blank">{{ $t('deviceCheck.footerNotes') }}</a>
    </div>
    <BottomMenu :hide-display="'left_center'" />
    <div class="preload-pdf-container" v-if="isAssessmentLesson || isLevel0">
      <object
        type="application/pdf"
        v-for="pdf in pdfToPreLoad"
        :data="pdf.url"
        v-bind:key="pdf.id"
        width="0px"
        height="0px"
      ></object>
    </div>
    <v-tour
      name="deviceSettingTour"
      :steps="steps"
      v-if="enableTour"
      :options="tourOptions"
      :callbacks="tourCallbacks"
    ></v-tour>
     <!-- Device Error Dialog Guide-->
     <DeviceErrorDialog
      v-if="displayErrorDialog"
      :media-device="errorDialogDevice"
      @close-dialog="displayErrorDialog = false"
    />
  </div>
</template>

<script>
import { mapMutations, mapActions, mapState, mapGetters } from 'vuex';
import { debounce, store, isBrowserSupportSpeaker, sendUserAgentLogData } from '@/utils/common';

import deviceMixin from '@/mixins/device';
import logTransport from '@/mixins/logTransport';
import virtualBgMixin from '@/mixins/virtualBg';
import vueTourMixins from '@/mixins/vueTour';

import BottomMenu from '@/components/Lesson/Footer/BottomMenu';
import LessonHeader from '@/components/Lesson/Header/LessonHeader';
import Waveform from '@/components/Lesson/Device/Waveform';
import ExcalamationSvg from '@/assets/img/icon/device_error_exclamation.svg';
import { getDeviceAccessInstance } from '@/utils/getDeviceAccess';
import DeviceErrorDialog from '@/components/Lesson/VideoChat/DeviceErrorDialog';
import deviceChangeEvent from '@/utils/deviceChangeEvent';
import logger from '@/modules/Logger';
import { getApiMethod } from '@/modules/apiSelector'
import { materialPreload } from '@/utils/constants';

export default {
  components: { 
    BottomMenu, 
    Waveform, 
    LessonHeader, 
    DeviceErrorDialog 
  },
  mixins: [logTransport, deviceMixin, vueTourMixins, virtualBgMixin],
  data() {
    return {
      cameraId: '',
      micId: '',
      speakerId: 'Speaker',
      audioStream: null,
      videoStream: null,
      firefoxScale: false,
      debounceResize: null,
      pdfToPreLoad: [],
      isLevel0: false,
      blurLoading: false,
      ExcalamationSvg: ExcalamationSvg,
      displayErrorDialog: false,
      errorDialogDevice: '',
      initialized: false,
      devicesReady: false,
      debounceDeviceChange: null,
      deviceChangeStop: null,
      lastDeviceList: []
    };
  },
  computed: {
    ...mapState([
      'localNickname', 
      'lessonId', 
      'lessonToken', 
      'isTest', 
      'isTrial',
      'studentRank',
      'studentLessonNo',
      'userId'
    ]),
    ...mapState('devices', [
      'cameras',
      'mics',
      'speakers',
      'cameraEnabled',
      'micEnabled',
      'selectedSpeaker'
    ]),
    ...mapState('videoRtc', ['virtualBgModel', 'virtualBg']),
    ...mapState('socketio', ['socket']),
    ...mapGetters('devices', [
      'camera', 
      'mic', 
      'speaker',
      'hasMicError',
      'hasCameraError',
      'cameraSelectPlaceHolder', 
      'micSelectPlaceHolder',
      'speakerSelectPlaceHolder',
      'isCameraPermissionDenied',
      'isMicPermissionDenied'
    ]),
    ...mapGetters('uaParser', ['isSafariBrowser', 'isFirefoxBrowser', 'isMobileDevice']),
    ...mapGetters(['isStudent', 'isLessonEnded', 'isAssessmentLesson', 'isLessonTest', 'jwtKeyValue', 'isZipanService']),
    ...mapGetters('layout', ['isDefaultLayout']),
    ...mapGetters('userOtherSetting', ['isTaiwanCountryId']),

    micIconSource() {
      return function (micEnabled) {
        const iconType = micEnabled ? 'on' : 'off';
        return require(`@/assets/img/mic_icn-${iconType}@2x.png`);
      };
    },
    cameraIconSource() {
      return function (cameraEnabled) {
        const iconType = cameraEnabled ? 'on' : 'off';
        return require(`@/assets/img/camera_icn-${iconType}@2x.png`);
      };
    },
    blurIconSource() {
      return function () {
        const iconType = this.blurEnabled ? 'off' : 'on';
        return require(`@/assets/img/blur_icn-${iconType}.svg`);
      };
    },

    disableCameraSelection() {
      return this.cameras.length === 0 || this.isCameraPermissionDenied;
    },

    disableMicSelection() {
      return this.isMicPermissionDenied || this.mics.length === 0;
    },

    disableSpeakerSelection() {
      return this.isMicPermissionDenied || this.speakers.length === 0;
    },

    cameraName() {
      return this.cameras.filter(camera => camera.deviceId.includes(this.cameraId))[0].label;
    },

    micName() {
      return this.mics.filter(mic => mic.deviceId.includes(this.micId))[0].label;
    },

    blurEnabled() {
      return this.virtualBg === 'blur';
    },
    cameraModel: {
      get () {
        return this.hasCameraError ? null : this.cameraId
      },
      set (val) {
        this.cameraId = val;
      }
    },
    micModel: {
      get () {
        return this.hasMicError ? null : this.micId;
      },
      set (val) {
        this.micId = val;
      }
    },
    joinBtnText() {
      return this.initialized ? 'deviceCheck.enter' : 'deviceCheck.connectingCamera';
    },
    faqLink() {
      if (this.isZipanService) {
        return 'https://supportbot-admin.userlocal.jp/pages/cfedf72a8faaa713c095?q=MyStageの機能・使用方法';
      }
      
      return this.isTaiwanCountryId 
        ? 'https://bizmates-taiwan.zendesk.com/hc/ja/sections/50157148523545-Mystage'
        : `https://supportbot-admin.userlocal.jp/pages/cfedf72a8faaa713c095?q=MyStageの不具合を改善したいです`;
    }
  },

  created() {
    if (!this.isLessonEnded) {
      this.loadMaterials();
    }
  },

  beforeMount: async function () {
    this.steps = this.tourSteps[this.$i18n.locale].deviceCheck;

    if (this.isLessonEnded) {
      this.$emit('join');
    }
    document.body.classList.toggle('scrollable', true);
    this.debounceResize = debounce(this.fitPageContent, 50);
    this.fitPageContent();
  },

  mounted() {
    this.addLogTransport();
    this.$logger.info('User has entered the MyStage Device Settings page', { priorityLog: true });
    this.removeLogTransport();
    
    sendUserAgentLogData(this.isStudent);

    if (!this.isLessonEnded) {
      this.initialize();
      this.debounceDeviceChange = debounce(this.deviceChanges, 1000);
      this.deviceChangeStop = deviceChangeEvent(this.debounceDeviceChange);
      window.addEventListener('resize', this.debounceResize);
    }

    this.setupTour('deviceSettingTour');
  },

  watch: {
    mic(newVal) {
      if (newVal) {
        this.micId = newVal.deviceId;
      }
    },
    camera(newVal) {
      if (newVal) {
        this.cameraId = newVal.deviceId;
      }
    },
    speakerId(newVal) {
      if (newVal) {
        this.changeSpeaker();
      }
    },
    cameras(newVal) {
      if (newVal.length === 0) {
        this.cameraId = '';
        this.setCameraEnabled(false);
      }
    },
    mics(newVal) {
      if (newVal.length === 0) {
        this.micId = '';
        this.setMicEnabled(false);
      }
    },
    videoStream: {
      handler(newVal) {
        if (newVal) {
          this.applyCameraSettings(newVal);
        }
      }
    },
    audioStream: {
      handler(newVal) {
        if (newVal) {
          this.applyMicSettings(newVal);
        }
      }
    }
  },

  beforeDestroy() {
    this.deviceChangeStop && this.deviceChangeStop();
    this.resetDesignAdjustment();

    this.destroyTour();
  },

  methods: {
    ...mapMutations('devices', [
      'selectMic',
      'selectCamera',
      'selectSpeaker',
      'setCameraEnabled',
      'setMicEnabled'
    ]),
    ...mapMutations('socketio', ['setRemoteUserJoined']),
    ...mapActions('devices', [
      'getMics', 
      'getCameras', 
      'getSpeakers', 
      'setMediaDeviceError',
      'selectExternalMic'
    ]),
    ...mapMutations('videoRtc', ['setVirtualBgModel', 'setVirtualBg']),
    ...mapState([
      'isApiMigrationCanaryUser'
    ]),

    async loadMaterials() {
      if (!this.isAssessmentLesson) {
        return;
      }

      const booking_id = this.$route.params.lessonId;
      const apiMethod = getApiMethod('getMaterial');
      await apiMethod(booking_id, 0, this.isTrial, (data) => {
        if (!data[0].lv && data[0].rk > 0) {
          this.isLevel0 = true;
          data.forEach((el) => {
            el.ls.forEach((item, idx) => {
              if (item.mi && idx < 3) {
                this.loadPdf(item.mi);
              }
            });
          });
        }
        if (data) {
            this.ranks = data;
            if (data && data[0] && data[0].ls) {
              data[0].ls.forEach((item) => {
                if (item.mi) {
                  this.loadPdf(item.mi);
                }
              });
          } else {
            this.$logger.error(
                `[Materials][Rank ID ${this.studentRank}][Lesson No ${this.studentLessonNo}] Empty result from materials api`,
                { operation: 'materials:get',event_type:4,lesson_id:this.lessonId }
              );
          }
        }
      }, this.jwtKeyValue, materialPreload.default);
      // supposed to be not included, see https://github.com/bizmatesinc/mystage-frontend/commit/c35482a019faa7ef55dac635517650bf17e924a8
      // this.displayCurrentRankLesson();
    },

    async loadPdf(paramMaterialId) {
      // do not execute if we dont have material ID data
      if (!this.lessonMaterialId && !paramMaterialId) {
        return;
      }
      
      const lessonId = this.$route.params.lessonId;
      const materialId = paramMaterialId ? paramMaterialId : this.lessonMaterialId;
      
      const apiMethod = getApiMethod('getPdfData');
      await apiMethod(
        lessonId, 
        materialId, 
        this.isTrial, 
        this.isLessonTest, 
        this.isDefaultLayout, 
        this.jwtKeyValue,
        (data) => {
          if (data && (typeof data === 'object' && Object.keys(data).length > 0)) {
            this.pdfToPreLoad.push({
              id: materialId,
              url: data.url
            });
          } else {
            logger.error(
              `[Materials][pdf][Rank ID ${this.studentRank}][Lesson No ${this.studentLessonNo}] Empty result from materials PDF data api`,
              { operation: 'materials:get_pdf' }
            );
          }
        }); 
        
      logger.info(`[Materials][pdf] Load PDF`, {
        operation: 'materials:get_pdf',
      });
    },

    async deviceChanges() {
      const currentDevices = await navigator.mediaDevices.enumerateDevices();
      if (this.isDeviceListUnchanged(this.lastDeviceList, currentDevices)) {
        return;
      }

      this.lastDeviceList = currentDevices;
      this.initialize();
    },

    async initialize() {
      // persistent feature of buttons
      const videoEffect = store.getBlurState();
      this.blurLoading = videoEffect !== 'none';
      this.setMicEnabled(store.getMicState());
      this.setCameraEnabled(store.getCameraState());

      await this.stopVideoStream();
      await this.stopAudioStream();
      //this is to pop-up the permision
      await this.populateDropdown();

      await this.createAudioStream(true);
      await this.createVideoStream(true);

      // this is to repopulate the dropdown due to browser security
      await this.populateDropdown();

      // set active media devices
      await Promise.all([
        this.setActiveMicDevice(),
        this.setActiveCameraDevice(),
        this.setActiveSpeakerDevice()
      ]);

      this.devicesReady = true;

      if (videoEffect !== 'none' && !this.hasCameraError) {
        setTimeout(() => this.setBlur(true), 200)
      } else {
        this.blurLoading = false;
        this.setVirtualBg('none');
      }

      this.initialized = true;
    },

    async setActiveMicDevice() {
      if (!this.mics.length) {
        return;
      }

      const lastActiveMicId = store.getMicId(this.userId);
      const lastActiveMicData = this.mics.find(key => key.deviceId === lastActiveMicId);

      this.micId = lastActiveMicData?.deviceId ?? this.mics[0].deviceId;
      await this.changeMic();

      // Set last listed microphone as active on mobile
      if (this.isMobileDevice) {
        const isManuallySelected = store.get(`mystage_micManual_${this.userId}`, null) === 'true';
        if (!(isManuallySelected && lastActiveMicData)) {
          this.micId = await this.selectExternalMic();
        }
      }
    },

    async setActiveSpeakerDevice() {
      if (!this.speakers.length) {
        return;
      }

      let lastActiveSpeakerId = store.getSpeakerId(this.userId);
      
      // find out if last speaker is still accessible
      let isLastActiveSpeakerValid = this.speakers.find(speaker => speaker.deviceId === lastActiveSpeakerId);
      lastActiveSpeakerId = isLastActiveSpeakerValid ? lastActiveSpeakerId : null;

      const defaultSpeaker = this.speakers.find(speaker => speaker.isDefault === true);

      // Determine the speaker ID
      const speakerId = lastActiveSpeakerId ?? defaultSpeaker?.deviceId ?? this.speakers[0]?.deviceId;

      let audioElement = document.querySelector('audio');
      await this.attachSinkId(audioElement, speakerId);
      this.speakerId = speakerId;
    },

    async setActiveCameraDevice() {
      if (this.cameras.length) {
        const lastActiveCameraId = store.getCameraId(this.userId);
        const lastActiveCameraData = this.cameras.find(key => key.deviceId === lastActiveCameraId);

        this.cameraId = lastActiveCameraData?.deviceId ?? this.cameras[0].deviceId;
        await this.changeCamera();
      }
    },

    async changeCamera() {
      await this.stopVideoStream();
      await this.createVideoStream({ deviceId: this.cameraId ? { exact: this.cameraId } : undefined });
      const camera = this.cameras.find(cam => cam.deviceId === this.cameraId);
      this.selectCamera(camera ? camera.label : this.cameraId);
    },

    async changeMic() {
      await this.stopAudioStream();
      await this.createAudioStream({ deviceId: this.micId ? { exact: this.micId } : undefined });
      const mic = this.mics.find(m => m.deviceId === this.micId);
      this.selectMic(mic ? mic.label : this.micId);
    },

    async changeSpeaker() {
      let audioElement = document.querySelector('audio');
      await this.attachSinkId(audioElement, this.speakerId);
      let speaker = this.speakers.filter(speaker => speaker.deviceId === this.speakerId);
      if (speaker?.[0]) {
        this.selectSpeaker(speaker[0].label);
      }
    },

    onManualCameraChange() {
      this.changeCamera();
      store.setCameraId(this.userId, this.cameraId, true);
    },

    onManualMicChange() {
      this.changeMic();
      store.setMicId(this.userId, this.micId, true);
    },

    onManualSpeakerChange() {
      store.setSpeakerId(this.userId, this.speakerId, true);
    },

    async stopVideoStream() {
      if (this.videoStream) {
        await this.videoStream.getTracks().forEach(track => {
          track.stop();
        });
      }
    },

    async stopAudioStream() {
      if (this.audioStream) {
        await this.audioStream.getTracks().forEach(track => {
          track.stop();
        });
      }
    },

    async createVideoStream(videoConstraint) {
      let vm = this;
      await navigator.mediaDevices
        .getUserMedia({ video: videoConstraint, audio: false })
        .then(function (mediaStream) {
          vm.setMediaDeviceError({ device:"video" });
          vm.videoStream = mediaStream;
          let video = document.getElementById('local-video');
          if (!video) return;
          video.srcObject = mediaStream;
          video.muted = true;
          video.onloadedmetadata = async () => {
            video.play();
            if (vm.blurEnabled) {
              vm.virtualBgModel.disposeInitialCanvas();
              await vm.virtualBgModel.performSegmentation(vm.cameraId);
            }
          };
        })
        .catch(function (err) {
          getDeviceAccessInstance.handleDeviceError("video", err);
          vm.setCameraEnabled(false);
        });
    },  

    async createAudioStream(audioConstraint) {
      let vm = this;
      await navigator.mediaDevices
        .getUserMedia({ audio: audioConstraint, video: false })
        .then(function (mediaStream) {
          vm.setMediaDeviceError({ device: "audio" });
          vm.audioStream = mediaStream;
        })
        .catch(function (err) {
          getDeviceAccessInstance.handleDeviceError("audio", err);
          if (err.name !== 'OverconstrainedError') {
            vm.micId = '';
            vm.setMicEnabled(false);
          }
        });
    },

    applyCameraSettings(stream) {
      if (stream.getVideoTracks().length > 0) {
        this.selectCamera(stream.getVideoTracks()[0].label);
        stream.getVideoTracks()[0].enabled = this.cameraEnabled;
      } else {
        this.selectCamera(null);
      }
    },

    applyMicSettings(stream) {
      if (stream.getAudioTracks().length > 0) {
        this.selectMic(stream.getAudioTracks()[0].label.replace('Default - ', ''));
        stream.getAudioTracks()[0].enabled = this.micEnabled;
      } else {
        this.selectMic(null);
      }
    },

    async populateDropdown() {
      await this.getMics();
      await this.getCameras();
      await this.getSpeakers();
    },

    toggleCamera() {
      if (this.hasCameraError) {
        this.errorDialogDevice = 'camera';
        this.displayErrorDialog = true;
        return;
      }

      if (this.disableCameraSelection || this.cameraId == '' && !this.isMobileDevice) {
        return;
      }
      
      this.videoStream.getVideoTracks()[0].enabled = !this.cameraEnabled;
      this.setCameraEnabled(!this.cameraEnabled);
      store.setCameraState(this.cameraEnabled);

      const cameraStatus = this.cameraEnabled ? 'on' : 'off';
      this.$logger.info(`User turned the camera ${cameraStatus}`, { operation: 'device:check', cameraName: this.cameraName });
    },

    toggleMic() {
      if (this.hasMicError) {
        this.errorDialogDevice = 'mic';
        this.displayErrorDialog = true;
        return;
      }
      
      if (this.disableMicSelection || this.micId == '' && !this.isMobileDevice) {
        return;
      }
      
      this.audioStream.getAudioTracks()[0].enabled = !this.micEnabled;
      this.setMicEnabled(!this.micEnabled);
      store.setMicState(this.micEnabled);

      const micStatus = this.micEnabled ? 'unmuted' : 'muted';
      this.$logger.info(`User ${micStatus} the microphone`, { operation: 'device:check', micName: this.micName });
    },

    async toggleBlur() {
      await this.setBlur(!this.blurEnabled);
      store.setBlurState(this.virtualBg);
      this.$logger.info(`User set background to ${this.virtualBg}`, { operation: 'device:check' });
    },

    async setBlur(enabled) {
      this.blurLoading = true;

      const hardwareAcceleration = await this.loadVBModel(true);
      if (!hardwareAcceleration) {
        this.blurLoading = false;
        return;
      }

      if (enabled) {
        await this.virtualBgModel.setEffect('blur').performSegmentation(this.cameraId);
      }

      this.setVirtualBg(enabled ? 'blur' : 'none');
      this.blurLoading = false;
    },

    playSound() {
      document.querySelector('audio').play();
      if (isBrowserSupportSpeaker()) {
        this.$logger.info('User test the speaker', { operation: 'device:check', speakerName: this.selectedSpeaker });
      }
    },

    async joinLesson() {
      if (!this.initialized) {
        // cant join lesson yet
        // devices need to be initialized
        // check the computed property for more detail
        return false;
      }
      await this.stopVideoStream();
      await this.stopAudioStream();
      store.set('lesson_id', this.lessonId);
      store.setCameraState(this.cameraEnabled);
      store.setMicState(this.micEnabled);
      store.setBlurState(this.virtualBg);
      this.setRemoteUserJoined(false);

      if (this.virtualBgModel) {
        this.virtualBgModel.disposeInitialCanvas();
      }
      store.setMicId(this.userId, this.micId);
      store.setCameraId(this.userId, this.cameraId);
      // log device check even before lesson start
      this.addLogTransport();
      const payload = { operation: 'device:check', cameraId: this.cameraId, micId: this.micId, priorityLog: true };
      if (isBrowserSupportSpeaker()) {
        payload.speakerId = this.speakerId;
        store.setSpeakerId(this.userId, this.speakerId);
        this.$logger.debug('Speaker Local Storage', { speakerId: store.getSpeakerId(this.userId) })
      }

      this.socket.emit('signal', { type: 'camera', state: this.cameraEnabled })
      this.socket.emit('signal', { type: 'mic', state: this.micEnabled })

      this.$logger.info('User has completed the device check and entered the lesson room', payload);
      this.removeLogTransport();
      this.$emit('join');
    },

    displayBottomErrorMessage() {
      return this.isStudent && (this.hasCameraError || this.hasMicError);
    },

    fitPageContent() {
      if (!this.isMobileDevice) {
        const zoom = window.innerWidth / window.outerWidth;
        document.body.style.zoom = zoom < 0.7 && zoom > 0.6 ? 0.82 : 1;
      }

      if (this.isFirefoxBrowser) {
        this.firefoxScale = Math.round(window.devicePixelRatio * 100) > 170;
      }
    },

    // reset adjustment from device-check
    resetDesignAdjustment() {
      document.body.classList.toggle('scrollable', false);
      document.body.style.zoom = 1;
      window.removeEventListener('resize', this.debounceResize);
    }
  }
};
</script>
<style scoped src="@/assets/css/pc/lesson_join.css"></style>
<style scoped src="@/assets/css/sp/lesson_join.css"></style>
